import { useState, useEffect, useMemo } from "react";
import { ArrowRight, PlusCircle, Clock, TrendingUp, ChevronDown, AlertCircle } from "lucide-react";

export default function P2PMarketCountdown({ 
  onAdd, 
  onGoToMarket, 
  marketLoading, 
  targetDate = new Date(Date.now() + 24 * 60 * 60 * 1000),
  closingTime = new Date(Date.now() + 12 * 60 * 60 * 1000) // Default to 12 hours from now
}) {
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    total: 0,
  });
  const [isExpired, setIsExpired] = useState(false);
  const [isMarketClosed, setIsMarketClosed] = useState(false);
  const [animation, setAnimation] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Store the initial time differences
  const initialTimeDiff = useMemo(() => targetDate.getTime() - Date.now(), [targetDate]);
  const initialClosingDiff = useMemo(() => closingTime.getTime() - Date.now(), [closingTime]);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();
      const closingDifference = closingTime.getTime() - now.getTime();

      // Check if market has closed
      if (closingDifference <= 0) {
        setIsMarketClosed(true);
      }

      if (difference <= 0) {
        setIsExpired(true);
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          total: 0
        };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        total: difference,
      };
    };

    setTimeRemaining(calculateTimeRemaining());

    const timerInterval = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);

      if (remaining.total <= 0 && !animation) {
        clearInterval(timerInterval);
        setAnimation(true);
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [targetDate, closingTime, animation]);

  const handleAddInvestment = () => {
    onAdd?.();
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const formatDigit = (value) => value < 10 ? `0${value}` : value;

  const calculateProgress = () => {
    if (initialTimeDiff <= 0) return 100;
    const elapsed = initialTimeDiff - timeRemaining.total;
    return Math.min(100, Math.max(0, (elapsed / initialTimeDiff) * 100));
  };

  // Calculate time until market closes
  const calculateTimeUntilClose = () => {
    const now = new Date();
    const difference = closingTime.getTime() - now.getTime();
    
    if (difference <= 0) return { hours: 0, minutes: 0, seconds: 0 };

    return {
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  const timeUntilClose = calculateTimeUntilClose();
  const marketStatus = isMarketClosed ? "Closed" : "Open";

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header with status indicator and collapse toggle */}
      <div
        className={`px-6 py-4 flex justify-between items-center cursor-pointer ${
          isMarketClosed ? "bg-gradient-to-r from-red-50 to-red-100" : "bg-gradient-to-r from-green-50 to-green-100"
        }`}
        onClick={toggleCollapse}
      >
        <div className="flex items-center">
          <Clock className={`mr-2 ${isMarketClosed ? "text-red-600" : "text-green-600"}`} size={20} />
          <div>
            <h2 className="text-lg font-medium text-gray-800">Market status</h2>
            <p className="text-xs text-gray-600">
              {isMarketClosed ? (
                "Market has closed"
              ) : (
                `Closes in ${timeUntilClose.hours}h ${timeUntilClose.minutes}m`
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center">
          <span className={`h-2 w-2 rounded-full mr-2 ${
            isExpired ? "bg-green-500" : 
            isMarketClosed ? "bg-red-500" : "bg-amber-500 animate-pulse"
          }`}></span>
          <span className="text-sm text-gray-600 mr-3">
            {isExpired ? "Ready" : isMarketClosed ? "Closed" : "Loading"}
          </span>
          <ChevronDown
            size={20}
            className={`text-gray-500 transition-transform duration-200 ${isCollapsed ? "" : "rotate-180"}`}
          />
        </div>
      </div>

      {/* Collapsible content */}
      {!isCollapsed && (
        <div className="px-6 py-8">
          {isMarketClosed ? (
            <div className="mb-6 p-4 bg-red-50 rounded-lg flex items-start">
              <AlertCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={18} />
              <div>
                <h3 className="font-medium text-red-800">Market Closed</h3>
                <p className="text-sm text-red-600">
                  The market has closed.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="relative h-2 bg-gray-100 rounded-full w-full mb-8">
                <div
                  className="absolute top-0 left-0 h-2 bg-green-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${calculateProgress()}%` }}
                ></div>
                <div 
                  className="absolute -top-1 h-4 w-4 bg-white border-2 border-green-500 rounded-full shadow-sm transition-all duration-500 ease-out"
                  style={{ left: `calc(${calculateProgress()}% - 8px)` }}
                ></div>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-8">
                <div className="bg-gray-50 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-3xl font-bold text-gray-800 mb-1">{formatDigit(timeRemaining.days)}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Days</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-3xl font-bold text-gray-800 mb-1">{formatDigit(timeRemaining.hours)}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Hours</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-3xl font-bold text-gray-800 mb-1">{formatDigit(timeRemaining.minutes)}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Minutes</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-3xl font-bold text-gray-800 mb-1">{formatDigit(timeRemaining.seconds)}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Seconds</div>
                </div>
              </div>
            </>
          )}

          {/* Action buttons */}
          <div className="flex gap-4">
            <button
              onClick={onGoToMarket}
              disabled={!isExpired || isMarketClosed}
              className={`flex-1 ${
                isExpired && !isMarketClosed
                  ? "bg-green-600 hover:bg-green-700 text-white shadow-lg transform transition-all duration-300 hover:scale-[1.01]"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              } py-3 px-4 rounded-lg flex items-center justify-center font-medium`}
            >
              {isExpired && !marketLoading && !isMarketClosed && (
                <TrendingUp size={18} className="mr-2 animate-pulse" />
              )}
              {!marketLoading && !isMarketClosed && 'Go to Market'}
              {isExpired && !marketLoading && !isMarketClosed && (
                <ArrowRight size={18} className="ml-2" />
              )}
              {marketLoading && <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>}
              {isMarketClosed && "Market Closed"}
            </button>

            <button
              onClick={handleAddInvestment}
              className={`flex items-center justify-center py-3 px-4 rounded-lg text-green-600 hover:text-green-700 bg-white border border-green-300 hover:border-green-500 shadow-sm hover:shadow font-medium transition-all duration-300`}
            >
              <PlusCircle size={18} className="mr-2" />
              Add Bid
            </button>
          </div>
        </div>
      )}
    </div>
  );
}